/**
 * @packageDocumentation
 * @module      ValidationTracker.ts
 * @description Tracks validations states for a page.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
/**
 * Tracks validations states for a page.
 */
export default class ValidationTracker {
    constructor() {
        /** An object whose keys are the ones passed to the errorAttribute function and whose Boolean value
         * record the results of validation (true if valid, false if not).
         */
        this.validationResults = {};
        this.validationResultsValueChanged = {};
        // Prefixes to add to keys
        this.prefixNonMandatory = 'nonMandatory:';
        this.prefixMandatory = 'mandatory:';
        this.prefixError = 'error:';
    }
    /** Return true if all fields are valid, or if none are registered; false otherwise. */
    get allValid() {
        for (const result of Object.values(this.validationResults)) {
            if (!result) {
                return false;
            }
        }
        return true;
    }
    /** Reseting the value of validationResultsValueChanged */
    resetPreviousValidationResults() {
        this.validationResultsValueChanged = {};
    }
    /**Returns true, if any of values of validationResultsValueChanged is changed */
    get fieldValueChanged() {
        let valid = false;
        for (const key of Object.keys(this.validationResultsValueChanged)) {
            valid = (valid || this.validationResultsValueChanged[key]);
        }
        return valid;
    }
    /** Return true if an invalid field has been detected; false otherwise. */
    get anyInvalid() {
        return !this.allValid;
    }
    /** Return true if an invalid field has been detected or changes made and all field are valid in Edit mode; false otherwise. */
    isSaveNotAllowed(isEditing) {
        return isEditing ? (this.anyInvalid || !this.fieldValueChanged) : this.anyInvalid;
    }
    /**
     * Install a validation function with the given key.
     *
     * Calling this function will install the given validator (isValid) in the error attribute of a Shui
     * element and cause the result of the validation to be tracked in this object so they participate
     * in the evaluation of the allValid() function.
     *
     * @param key A unique key by which this class will be able to look up validation results.
     * @param isValid A function that returns true if the item is valid; false if not.
     *
     * @returns A function whose return value is suitable for installing in a Shui error attribute.
     *
     */
    error(key, isValid) {
        const validationResult = !!isValid();
        // @ts-ignore
        this.validationResults[this.prefixError + key] = validationResult;
        return !validationResult; // Flip true/false because false means there IS an error.
    }
    /**
     * Make a field always have the 'mandatory' attribute, and also track the results.
     *
     * @param key A unique key by which this class will be able to look up validation results
     * @param getString A function that returns the value of the input element.
     * @param getPreviousString A function that returns the value of the input element before clicking Edit/Add.
     *
     * @returns Always true, so you can do this:
     *          ?mandatory=${tracker.alwaysMandatoryString('myField', () => this.getMyField()) }
     */
    alwaysMandatoryString(key, getString, getPreviousString) {
        const val = getString();
        const previousVal = getPreviousString();
        // @ts-ignore
        this.validationResults[this.prefixMandatory + key] = !!val;
        this.validationResultsValueChanged[this.prefixMandatory + key] = (val != previousVal);
        return true;
    }
    /**
     * Make a field have the 'nonMandatory' attribute, and also track the results.
     *
     * @param key A unique key by which this class will be able to look up validation results
     * @param getString A function that returns the value of the input element.
     * @param getPreviousString A function that returns the value of the input element before clicking Edit/Add.
     */
    nonMandatoryString(key, getString, getPreviousString) {
        const val = getString();
        const previousVal = getPreviousString();
        // @ts-ignore
        this.validationResultsValueChanged[this.prefixNonMandatory + key] = (val != previousVal);
    }
    /**
     * Return the keys that were installed for mandatory-field validation
     */
    get mandatoryKeys() {
        return this.getKeys(this.prefixMandatory);
    }
    /**
     * Return the keys that were installed for error validation
     */
    get errorKeys() {
        return this.getKeys(this.prefixError);
    }
    getKeys(prefix) {
        return Object.keys(this.validationResults)
            .filter((key) => key.startsWith(prefix))
            .map((key) => key.substr(prefix.length));
    }
}
//# sourceMappingURL=ValidationTracker.js.map